import { Component, Input, Inject, NgZone, OnDestroy, PLATFORM_ID, ChangeDetectorRef, forwardRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5Local from '@amcharts/amcharts5/locales/es_ES';
import { DashboardBaseChart } from '../shared/dashboard-base-chart';
import { DashboardBaseComponent } from '../shared/dashboard-base-component';
import {ChartLoadingComponent} from "../chart-loading/chart-loading.component";

export interface ChartTwoLinkedPieConfig {
  /** Unique DOM id for the amCharts root */
  chartId: string;
  /** Each item must have: category (string), value (number), side ('left'|'right') */
  data: any[];
  /** When set, formats the tooltip value */
  tooltipValueFormatter?: (v: number) => string;
}

export interface ChartTwoLinkedPieCardConfig {
  title: string;
  sub?: string;
  lastUpdated?: string;
  chart: ChartTwoLinkedPieConfig;
}

@Component({
  selector: 'app-chart-two-linked-pie',
  templateUrl: './chart-two-linked-pie.component.html',
  styleUrls: ['./chart-two-linked-pie.component.scss'],
  standalone: true,
  imports: [
    ChartLoadingComponent
  ],
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => ChartTwoLinkedPieComponent)
    }
  ]
})
export class ChartTwoLinkedPieComponent extends DashboardBaseChart implements OnDestroy {
  @Input() config!: ChartTwoLinkedPieConfig;

  isLoading = true;

  private series0?: am5percent.PieSeries;
  private series1?: am5percent.PieSeries;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  protected initChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.config?.chartId) return;

    this.zone.runOutsideAngular(() => {
      const root = am5.Root.new(this.config.chartId);
      root.locale = am5Local;
      if (root._logo) root._logo.dispose();

      const myTheme = am5.Theme.new(root);
      myTheme.rule('Label').setAll({ fontFamily: 'Roboto', fill: am5.color('#d9e8ff') });
      root.setThemes([am5themes_Animated.new(root), myTheme]);
      this.root = root;

      const container = root.container.children.push(
        am5.Container.new(root, {
          width: am5.percent(100),
          height: am5.percent(100),
          layout: root.horizontalLayout,
        })
      );

      const makeTooltip = () => {
        const tt = am5.Tooltip.new(root, { labelText: '[bold]{category}: {value}' });
        const fmt = this.config.tooltipValueFormatter;
        if (fmt) {
          tt.label.adapters.add('text', (_text, target) => {
            const value = (target.dataItem as any)?.get('value') as number | undefined;
            const cat = (target.dataItem as any)?.get('category') as string | undefined;
            return value != null ? `[bold]${cat ?? ''}: ${fmt(value)}` : _text;
          });
        }
        return tt;
      };

      const chart0 = container.children.push(
        am5percent.PieChart.new(root, {
          innerRadius: am5.percent(50),
          tooltip: makeTooltip(),
        })
      );

      container.children.push(
        am5.Line.new(root, {
          layer: 1,
          height: am5.percent(60),
          y: am5.p50,
          centerY: am5.p50,
          strokeDasharray: [4, 4],
          stroke: am5.color('#9db8d7'),
          strokeOpacity: 0.5,
        })
      );

      container.children.push(
        am5.Label.new(root, {
          layer: 1,
          text: 'Arrastra los sectores al otro gráfico',
          y: am5.p50,
          centerY: am5.p50,
          centerX: am5.p50,
          textAlign: 'center',
          rotation: -90,
          fill: am5.color('#9db8d7'),
          fontSize: 8,
          fontFamily: 'Roboto',
        })
      );

      const chart1 = container.children.push(
        am5percent.PieChart.new(root, {
          innerRadius: am5.percent(50),
          tooltip: makeTooltip(),
        })
      );

      const colorSet = am5.ColorSet.new(root, {
        colors: [
          am5.color('#00CFFF'), am5.color('#00B7F2'), am5.color('#00A2E8'),
          am5.color('#008CDB'), am5.color('#0075C9'), am5.color('#005BB5'),
          am5.color('#0044A0'), am5.color('#002F8C'),
        ],
      });

      const makeSeries = (chart: am5percent.PieChart): am5percent.PieSeries => {
        const s = chart.series.push(
          am5percent.PieSeries.new(root, {
            valueField: 'value',
            categoryField: 'category',
            alignLabels: false,
          })
        );
        s.set('colors', colorSet);
        s.labels.template.setAll({ textType: 'regular', radius: 25, fontSize: 10, fontFamily: 'Roboto' });
        s.ticks.template.set('forceHidden', true);
        s.slices.template.setAll({ draggable: true, cornerRadius: 5, strokeOpacity: 0 });
        return s;
      };

      this.series0 = makeSeries(chart0);
      this.series1 = makeSeries(chart1);

      this.setupDragInteraction(container);
    });
  }

  load(data: any[]): void {
    this.isLoading = false;
    this.cdr.detectChanges();
    if (!this.root) this.initChart();

    this.config.data = data;
    if (!this.series0 || !this.series1) return;
    this.series0.data.setAll(data.filter(d => d.side === 'left'));
    this.series1.data.setAll(data.filter(d => d.side === 'right'));
    this.isLoaded = true;
  }

  private setupDragInteraction(container: am5.Container): void {
    const handleDrag = (slice: any, toSeries: am5percent.PieSeries, side: 'left' | 'right') => {
      if (!slice.dataItem) return;
      const dataContext = { ...slice.dataItem.dataContext, side };
      slice.dataItem.hide();
      const existing = toSeries.dataItems.find((item: any) => item.dataContext?.category === dataContext.category);
      if (existing) {
        existing.show();
        existing.get('slice').setAll({ x: 0, y: 0 });
      } else {
        toSeries.data.push(dataContext);
      }
    };
    if (!this.series0 || !this.series1) return;
    [this.series0.slices.template, this.series1.slices.template].forEach(template => {
      template.events.on('pointerdown', (e) => e.target.set('layer', 1));
      template.events.on('pointerup', (e) => {
        const slice = e.target;
        if (!this.series0 || !this.series1) return;
        const isFromLeft = this.series0.slices.contains(slice);
        const toSeries = isFromLeft ? this.series1 : this.series0;
        const mid = container.width() / 2;
        if ((isFromLeft && slice.x() > mid / 2) || (!isFromLeft && slice.x() < mid / 2)) {
          handleDrag(slice, toSeries, isFromLeft ? 'right' : 'left');
        } else {
          slice.animate({ key: 'x', to: 0, duration: 500, easing: am5.ease.out(am5.ease.cubic) });
          slice.animate({ key: 'y', to: 0, duration: 500, easing: am5.ease.out(am5.ease.cubic) });
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.root?.dispose();
  }
}
